/**
 * Shared status filtering utilities
 * Centralizes status classification logic used across dashboards
 */

// Define status categories
export const STATUS_CATEGORIES = {
  OPEN: ['active', 'accepting_candidates', 'accepting candidates', 'open', 'pending', 'waiting'],
  IN_PROGRESS: ['interviewing', 'interview', 'covered', 'offer_out', 'offer out'], // Progressing well, no new submissions needed
  CLOSED: ['placed', 'filled', 'declined', 'cancelled', 'closed'],
  ON_HOLD: ['on_hold', 'paused', 'suspended']
};

// Export for backwards compatibility and direct use
export { STATUS_CATEGORIES as JOB_STATUS_CATEGORIES };

/**
 * Check if a job/entity has an open/active status
 * @param {Object} entity - The entity (job, candidate, etc.) with a status field
 * @returns {boolean}
 */
export const isOpenStatus = (entity) => {
  if (!entity || !entity.status) return false;
  
  const status = entity.status.toLowerCase();
  const closedStatuses = STATUS_CATEGORIES.CLOSED;
  
  // Check if status contains any closed status keywords
  return !closedStatuses.some(s => status.includes(s));
};

/**
 * Check if a job is in progress (interviewing, offers out, covered)
 * These jobs don't need new submissions
 * @param {Object} entity - The entity with a status field
 * @returns {boolean}
 */
export const isInProgressStatus = (entity) => {
  if (!entity || !entity.status) return false;
  
  const status = entity.status.toLowerCase();
  const inProgressStatuses = STATUS_CATEGORIES.IN_PROGRESS;
  
  return inProgressStatuses.some(s => status.includes(s));
};

/**
 * Check if a job/entity has a closed status
 * @param {Object} entity - The entity with a status field
 * @returns {boolean}
 */
export const isClosedStatus = (entity) => {
  if (!entity || !entity.status) return false;
  
  const status = entity.status.toLowerCase();
  const closedStatuses = [...STATUS_CATEGORIES.CLOSED, ...STATUS_CATEGORIES.IN_PROGRESS];
  
  return closedStatuses.some(s => status.includes(s));
};

/**
 * Check if a job needs submissions
 * Excludes jobs that are in-progress, covered, or closed
 * @param {Object} job - Job object
 * @returns {boolean}
 */
export const needsSubmissions = (job) => {
  if (!job || !job.status) return true; // Default to true if no status
  
  const status = job.status.toLowerCase();
  
  // These statuses mean the job doesn't need NEW submissions
  const noSubmissionsNeeded = [
    ...STATUS_CATEGORIES.IN_PROGRESS,
    ...STATUS_CATEGORIES.CLOSED,
    ...STATUS_CATEGORIES.ON_HOLD
  ];
  
  return !noSubmissionsNeeded.some(s => status.includes(s));
};

/**
 * Filter array of items by open/closed status
 * @param {Array} items - Array of items to filter
 * @param {string} filterMode - 'open', 'closed', or 'all'
 * @returns {Array}
 */
export const filterByStatus = (items, filterMode = 'all') => {
  if (filterMode === 'all') return items;
  
  return items.filter(item => {
    if (filterMode === 'open') return isOpenStatus(item);
    if (filterMode === 'closed') return isClosedStatus(item);
    return true;
  });
};

/**
 * Get status badge color based on status value
 * @param {string} status - Status string
 * @returns {Object} - Tailwind classes for badge styling
 */
export const getStatusBadgeStyle = (status) => {
  if (!status) return { bg: 'bg-gray-100', text: 'text-gray-800' };
  
  const statusLower = status.toLowerCase();
  
  // Open/Active statuses - green
  if (statusLower.includes('active') || statusLower.includes('accepting') || statusLower.includes('open')) {
    return { bg: 'bg-green-100', text: 'text-green-800' };
  }
  
  // Filled/Placed statuses - blue
  if (statusLower.includes('placed') || statusLower.includes('filled')) {
    return { bg: 'bg-blue-100', text: 'text-blue-800' };
  }
  
  // On hold - yellow
  if (statusLower.includes('hold') || statusLower.includes('paused')) {
    return { bg: 'bg-yellow-100', text: 'text-yellow-800' };
  }
  
  // Declined/Cancelled - red
  if (statusLower.includes('declined') || statusLower.includes('cancelled') || statusLower.includes('rejected')) {
    return { bg: 'bg-red-100', text: 'text-red-800' };
  }
  
  // Default - gray
  return { bg: 'bg-gray-100', text: 'text-gray-800' };
};
